import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    Selection,
    Export,
    Sorting,
    Paging,
    Toolbar,
    Item,
    Editing,
    FilterRow,
    StateStoring,
    ColumnChooser,
    ColumnChooserSearch,
    Lookup,
    RequiredRule
} from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import { createConcurrencyAwareStore, getStoreErrorMessage, refreshGridOnConflict } from "../../../../../common/dataStore";
import { LoadPanel } from 'devextreme-react/load-panel';
import { Button } from 'devextreme-react/button';
import DropDownButton from 'devextreme-react/drop-down-button';
import { Splitter, Item as SplitterItem } from "devextreme-react/splitter";
import { confirm } from 'devextreme/ui/dialog';
import data from "../../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { GridLayout } from "../../../../../common/GridLayout";
import GridLayoutManager from "../../../../../gridLayoutCustomization/GridLayoutManager";

const dayOfWeekOptions = [
    { id: 0, text: 'Monday' },
    { id: 1, text: 'Tuesday' },
    { id: 2, text: 'Wednesday' },
    { id: 3, text: 'Thursday' },
    { id: 4, text: 'Friday' },
    { id: 5, text: 'Saturday' },
    { id: 6, text: 'Sunday' },
];

const loadDurationTypeOptions = [
    { id: 'H', text: 'Hours' },
    { id: 'M', text: 'Minutes' },
    { id: 'D', text: 'Days' },
];

const timeEditorOptions = { type: 'time' };

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

// Default business hours for "Add Week" bulk-inserted day rows. Start/End are
// required (Start < End) both client- and server-side, so time-less rows would
// be rejected on insert; seed a 09:00–17:00 shift the user can adjust afterward.
const DEFAULT_START_HOUR = 9;
const DEFAULT_END_HOUR = 17;

// Build a "yyyy-MM-ddTHH:mm:ss" value (matches the grid's serializationFormat)
// for today at the given hour, so bulk-added times line up with manual entries.
function shiftTime(hour) {
    const d = new Date();
    d.setHours(hour, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(hour)}:00:00`;
}

// Cross-field grid validation: Start Time must be earlier than End Time.
// Cell editing sends only the changed cell, so merge oldData + newData first.
function validateStartBeforeEnd(e) {
    const row = { ...e.oldData, ...e.newData };
    if (row.StartTime && row.EndTime && new Date(row.StartTime) >= new Date(row.EndTime)) {
        e.isValid = false;
        e.errorText = "Start Time must be earlier than End Time.";
    }
}

function WeekDaysTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    // A real (saved) header is required before the child grids can add/focus rows.
    // Without it, focusing a keyless row throws DevExtreme E1042 on the Add page.
    const hasHeader = !!props.headerId && props.headerId !== EMPTY_GUID;
    const detailGrid = useRef(null);
    const dateGrid = useRef(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [selectedDetailKeys, setSelectedDetailKeys] = useState([]);
    const [selectedDetailRowId, setSelectedDetailRowId] = useState(null);
    const [selectedDateKeys, setSelectedDateKeys] = useState([]);
    const detailGridKey = "C1D2E3F4-A5B6-47C8-99D0-1E2F3A4B5C61";
    const dateGridKey = "D2E3F4A5-B6C7-48D9-A0E1-2F3A4B5C6D72";
    const [detailLayout, setDetailLayout] = useState("Default");
    const [dateLayout, setDateLayout] = useState("Default");

    // Selection: a plain click selects a single row (clears others); Ctrl/Shift+click
    // keeps multi-selection. We capture the modifier on mousedown (fires before the
    // grid's selection handler) and collapse the selection unless a modifier was held.
    const selectModifierRef = useRef(false);
    useEffect(() => {
        const onMouseDown = (ev) => { selectModifierRef.current = !!(ev.ctrlKey || ev.shiftKey || ev.metaKey); };
        document.addEventListener('mousedown', onMouseDown, true);
        return () => document.removeEventListener('mousedown', onMouseDown, true);
    }, []);

    const applySelection = useCallback((e, setKeys) => {
        if (!selectModifierRef.current && e.selectedRowKeys.length > 1) {
            const list = (e.currentSelectedRowKeys && e.currentSelectedRowKeys.length)
                ? e.currentSelectedRowKeys : e.selectedRowKeys;
            // Re-selecting fires onSelectionChanged again with a single key (length === 1),
            // so this does not loop.
            e.component.selectRows([list[list.length - 1]], false);
            return;
        }
        setKeys(e.selectedRowKeys);
    }, []);

    const position = { of: '#accordion' };

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    // ============================================================
    // First Grid: Week Settings Details (one row per day-of-week)
    // ============================================================
    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(detailGridKey, "GET", accessToken, null, detailLayout);
    }, [detailLayout, accessToken]);

    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(detailGridKey, "POST", accessToken, state, detailLayout);
    }, [detailGridKey, detailLayout, accessToken]);

    const dataSource = useMemo(() => {
        if (!props.headerId || props.headerId === '00000000-0000-0000-0000-000000000000') {
            return new DataSource({
                store: [],
                key: "RowId"
            });
        }

        return new DataSource({
            store: createConcurrencyAwareStore({
                onConflict: refreshGridOnConflict(detailGrid),
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.WeekSettingsDetail.GetWeekSettingsDetails}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.WeekSettingsDetail.AddWeekSettingsDetail}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.WeekSettingsDetail.EditWeekSettingsDetail}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.WeekSettingsDetail.DeleteWeekSettingsDetail}`,
                loadParams: {
                    weekSettingsHeaderRowId: props.headerId
                },
                // Auth headers and optimistic-concurrency detection come from the shared wrapper.
                // Standard DevExtreme protocol otherwise: the grid posts form-encoded values/key,
                // and parent FK + RowId are stamped in onRowInserting/onRowUpdating, not here.
                //
                // Insert/update run through the grid's inline editing, so their errors are
                // shown by the DataGrid's built-in error row. Delete is handled manually in
                // deleteSelectedRows (createStore has no per-op on*Error - only onRemoved fires).
                // No manual grid.refresh() here: the API returns the full row - including a
                // refreshed ConcurrencyToken - and DevExtreme patches it in place
                // (refreshMode="repaint"), so a reload would only add a redundant GET per edit.
                onInserted: function () {
                    notify("Week day setting inserted successfully.", "success", 3000);
                },
                onUpdated: function () {
                    notify("Week day setting updated successfully.", "success", 3000);
                },
                onRemoved: function () {
                    notify("Week day setting deleted successfully.", "success", 3000);
                }
            }, accessToken),
            key: "RowId",
            paginate: false
        });
    }, [props.headerId, accessToken]);

    // Checkbox selection is used only to enable the toolbar Delete button.
    const onSelectionChanged = useCallback((e) => {
        applySelection(e, setSelectedDetailKeys);
    }, [applySelection]);

    // The highlighted (focused) row drives the Date/Time grid below.
    const onFocusedRowChanged = useCallback((e) => {
        setSelectedDetailRowId(e.row ? e.row.key : null);
    }, []);

    const onInitNewRow = useCallback((e) => {
        e.data.RowId = '00000000-0000-0000-0000-000000000000';
        e.data.RWeeksettingsheader = props.headerId;
        e.data.DayOfWeek = null;
        e.data.LoadDurationType = 'H';
        e.data.LoadDuration = 1;
        e.data.StartTime = null;
        e.data.EndTime = null;
        e.data.Description = '';
        e.data.Comment = '';
    }, [props.headerId]);

    const onRowInserted = useCallback((e) => {
        if (detailGrid.current?.instance) {
            detailGrid.current.instance().option('focusedRowKey', e.key);
        }
    }, []);

    // Stamp the key + parent FK on insert so they land inside the posted "values" JSON.
    const onRowInserting = useCallback((e) => {
        e.data.RowId = EMPTY_GUID;
        e.data.RWeeksettingsheader = props.headerId;
    }, [props.headerId]);

    // Cell editing sends only the changed cell. Merge the existing row so the full
    // record is persisted; otherwise the unchanged columns are sent as null and wiped.
    // This merge is also what carries ConcurrencyToken from the loaded row into the PUT -
    // it needs no grid column of its own. Don't narrow this to a field whitelist.
    const onRowUpdating = useCallback((e) => {
        e.newData = { ...e.oldData, ...e.newData };
        e.newData.RWeeksettingsheader = props.headerId;
    }, [props.headerId]);

    // Focus (not checkbox-select) the row being edited so the child grid follows it.
    const onEditingStart = useCallback((e) => {
        const grid = detailGrid.current?.instance?.();
        if (grid && e.key != null && e.key != undefined) {
            grid.option('focusedRowKey', e.key);
        }
    }, []);

    const onContentReady = useCallback((e) => {
        if (!e.component._hasLoaded) {
            e.component._hasLoaded = true;
            if (e.component.getVisibleRows().length > 0 && (e.component.option('focusedRowIndex') ?? -1) < 0) {
                e.component.option('focusedRowIndex', 0);
            }
        }
    }, []);

    // Shared: confirm then delete the grid's checkbox-selected rows via its store.
    // A successful remove() fires the store's onRemoved (success toast); a failed one
    // rejects with the server message as a plain string, which we surface here - the
    // store has no per-op onRemoveError, and remove() bypasses the grid's error row.
    const deleteSelectedRows = useCallback((gridRef, label) => {
        const grid = gridRef.current?.instance?.();
        if (!grid) return;
        const keys = grid.getSelectedRowKeys();
        if (!keys.length) return;
        confirm("Delete selected record?", "Confirm delete").then((yes) => {
            if (!yes) return;
            const store = grid.getDataSource().store();
            Promise.allSettled(keys.map((k) => store.remove(k))).then((results) => {
                results.filter((r) => r.status === "rejected").forEach((r) => {
                    // Returns null when the failure was a concurrency conflict - the shared helper
                    // has already raised the dialog, so stay silent instead of also showing a red
                    // toast. The same refresh action the store was built with is passed here so a
                    // stale delete refreshes this grid, exactly like a stale edit does; without it
                    // the delete path would fall back to reloading the whole page.
                    const message = getStoreErrorMessage(
                        r.reason,
                        `Failed to delete ${label}.`,
                        refreshGridOnConflict(gridRef)
                    );
                    if (message) notify(message, "error", 5000);
                });
                grid.clearSelection();
                grid.refresh();
            });
        });
    }, []);

    const onDeleteSelectedDetails = useCallback(() => deleteSelectedRows(detailGrid, "week day setting"), [deleteSelectedRows]);

    // Options (gear): insert one detail row per missing day-of-week (Mon-Sun).
    const onAddWholeWeek = useCallback(() => {
        const grid = detailGrid.current?.instance?.();
        if (!grid) return;
        if (!props.headerId || props.headerId === EMPTY_GUID) {
            notify("Save the header first to add day settings.", "warning", 3000);
            return;
        }
        const existing = (grid.getDataSource().items() || []).map((r) => r.DayOfWeek);
        const missing = dayOfWeekOptions.map((o) => o.id).filter((d) => !existing.includes(d));
        if (!missing.length) {
            notify("All seven days already exist.", "info", 3000);
            return;
        }
        const store = grid.getDataSource().store();
        Promise.all(missing.map((d) => store.insert({
            RowId: EMPTY_GUID,
            RWeeksettingsheader: props.headerId,
            DayOfWeek: d,
            LoadDurationType: 'H',
            LoadDuration: 1,
            StartTime: shiftTime(DEFAULT_START_HOUR),
            EndTime: shiftTime(DEFAULT_END_HOUR),
            Description: '',
            Comment: ''
        }))).finally(() => grid.refresh());
    }, [props.headerId]);

    const onWeekOptionClick = useCallback((e) => {
        if (e.itemData && e.itemData.id === 'addWeek') onAddWholeWeek();
    }, [onAddWholeWeek]);

    // ============================================================
    // Second Grid: Week Settings Dates (based on selected day row)
    // ============================================================
    const loadStateDate = useCallback(() => {
        return GridLayout.sendStorageRequest(dateGridKey, "GET", accessToken, null, dateLayout);
    }, [dateLayout, accessToken]);

    const saveStateDate = useCallback((state) => {
        GridLayout.sendStorageRequest(dateGridKey, "POST", accessToken, state, dateLayout);
    }, [dateGridKey, dateLayout, accessToken]);

    const dateDataSource = useMemo(() => {
        if (!selectedDetailRowId) {
            return new DataSource({
                store: [],
                key: "RowId"
            });
        }

        return new DataSource({
            store: createConcurrencyAwareStore({
                onConflict: refreshGridOnConflict(dateGrid),
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.WeekSettingsDate.GetWeekSettingsDates}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.WeekSettingsDate.AddWeekSettingsDate}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.WeekSettingsDate.EditWeekSettingsDate}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.WeekSettingsDate.DeleteWeekSettingsDate}`,
                loadParams: {
                    weekSettingsDetailRowId: selectedDetailRowId
                },
                // Auth headers and optimistic-concurrency detection come from the shared wrapper.
                // Standard DevExtreme protocol otherwise: the grid posts form-encoded values/key,
                // and parent FK + RowId are stamped in onRowInserting/onRowUpdating, not here.
                //
                // Insert/update run through the grid's inline editing, so their errors are
                // shown by the DataGrid's built-in error row. Delete is handled manually in
                // deleteSelectedRows (createStore has no per-op on*Error - only onRemoved fires).
                // No manual grid.refresh(): the API returns the full row - including a refreshed
                // ConcurrencyToken - and DevExtreme patches it in place (refreshMode="repaint").
                onInserted: function () {
                    notify("Date/time entry inserted successfully.", "success", 3000);
                },
                onUpdated: function () {
                    notify("Date/time entry updated successfully.", "success", 3000);
                },
                onRemoved: function () {
                    notify("Date/time entry deleted successfully.", "success", 3000);
                }
            }, accessToken),
            key: "RowId",
            paginate: false
        });
    }, [selectedDetailRowId, accessToken]);

    const onDateSelectionChanged = useCallback((e) => {
        applySelection(e, setSelectedDateKeys);
    }, [applySelection]);

    const onDateInitNewRow = useCallback((e) => {
        e.data.RowId = '00000000-0000-0000-0000-000000000000';
        e.data.RWeeksettingsdetail = selectedDetailRowId;
        e.data.StartTime = null;
        e.data.EndTime = null;
        e.data.Description = '';
        e.data.Comment = '';
    }, [selectedDetailRowId]);

    const onDeleteSelectedDates = useCallback(() => deleteSelectedRows(dateGrid, "date/time entry"), [deleteSelectedRows]);

    const onWeekSettingsDateRowInserting = useCallback((e) => {
        e.data.RowId = EMPTY_GUID;
        e.data.RWeeksettingsdetail = selectedDetailRowId;
    }, [selectedDetailRowId]);

    // As above: the oldData merge is also what carries ConcurrencyToken into the PUT.
    const onDateRowUpdating = useCallback((e) => {
        e.newData = { ...e.oldData, ...e.newData };
        e.newData.RWeeksettingsdetail = selectedDetailRowId;
    }, [selectedDetailRowId]);

    const onDateEditingStart = useCallback(() => { }, []);

    const onDateContentReady = useCallback((e) => {
        if (!e.component._hasLoaded) {
            e.component._hasLoaded = true;
        }
    }, []);

    // Reset the second grid's loaded flag when the selected day changes
    useEffect(() => {
        if (dateGrid.current?.instance) {
            dateGrid.current.instance()._hasLoaded = false;
        }
    }, [selectedDetailRowId]);

    return (
        <div>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />

            <div style={{ height: '72vh' }}>
            <Splitter orientation="vertical">
              <SplitterItem resizable={true} collapsible={true} size="50%" minSize="100px">
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* First Grid: Week Settings Details (per day) */}
            <h6 style={{ margin: '10px 0 5px 0', fontWeight: 'bold' }}>Week Day Settings</h6>
                <div style={{ flex: '1 1 auto', minHeight: 0 }}>
            <DataGrid
                dataSource={dataSource}
                ref={detailGrid}
                selectedRowKeys={selectedDetailKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={onSelectionChanged}
                focusedRowEnabled={hasHeader}
                onFocusedRowChanged={onFocusedRowChanged}
                onInitNewRow={onInitNewRow}
                onRowInserting={onRowInserting}
                onRowInserted={onRowInserted}
                onRowUpdating={onRowUpdating}
                onRowValidating={validateStartBeforeEnd}
                onEditingStart={onEditingStart}
                onContentReady={onContentReady}
                height="100%"
                noDataText={(props.headerId && props.headerId !== '00000000-0000-0000-0000-000000000000') ? "No day settings found" : "Save the header first to add day settings"}
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={detailGridKey}
                    customSave={saveState}
                />
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={{ placeholder: 'Search column' }} />
                </ColumnChooser>
                <Selection mode="multiple" showCheckBoxesMode="always" allowSelectAll={false} />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowAdding={hasHeader}
                    allowUpdating={hasHeader}
                    allowDeleting={false}
                    newRowPosition="last"
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="RowId" caption="Row ID" visible={false} showInColumnChooser={false} />
                <Column dataField="RWeeksettingsheader" caption="Header ID" visible={false} showInColumnChooser={false} />
                <Column
                    dataField="RecordNumber"
                    caption="Record #"
                    dataType="string"
                    allowEditing={false}
                    allowSorting={false}
                />
                <Column
                    dataField="DayOfWeek"
                    caption="Day"
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={dayOfWeekOptions}
                        valueExpr="id"
                        displayExpr="text"
                    />
                </Column>
                <Column
                    dataField="LoadDurationType"
                    caption="Load Type"
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={loadDurationTypeOptions}
                        valueExpr="id"
                        displayExpr="text"
                    />
                </Column>
                <Column
                    dataField="LoadDuration"
                    caption="Load Duration"
                    dataType="number"
                    allowSorting={false}
                />
                <Column
                    dataField="StartTime"
                    caption="Start Time"
                    dataType="datetime"
                    serializationFormat="yyyy-MM-ddTHH:mm:ss"
                    format="shortTime"
                    editorOptions={timeEditorOptions}
                    allowSorting={false}
                >
                    <RequiredRule message="Start Time value must be specified." />
                </Column>
                <Column
                    dataField="EndTime"
                    caption="End Time"
                    dataType="datetime"
                    serializationFormat="yyyy-MM-ddTHH:mm:ss"
                    format="shortTime"
                    editorOptions={timeEditorOptions}
                    allowSorting={false}
                >
                    <RequiredRule message="End Time value must be specified." />
                </Column>
                <Column
                    dataField="Description"
                    caption="Description"
                    dataType="string"
                    allowSorting={false}
                />
                <Column
                    dataField="Comment"
                    caption="Comment"
                    dataType="string"
                    allowSorting={false}
                />
                <Toolbar>
                    <Item name="addRowButton" location="after" />
                    <Item location="after" locateInMenu="never">
                        <Button
                            icon="trash"
                            hint="Delete selected"
                            stylingMode="text"
                            disabled={selectedDetailKeys.length === 0}
                            onClick={onDeleteSelectedDetails}
                        />
                    </Item>
                    <Item location="after" locateInMenu="never">
                        <DropDownButton
                            icon="preferences"
                            hint="Options"
                            stylingMode="text"
                            showArrowIcon={true}
                            displayExpr="text"
                            keyExpr="id"
                            items={[{ id: 'addWeek', text: 'Add Week (Mon–Sun)' }]}
                            onItemClick={onWeekOptionClick}
                            dropDownOptions={{ width: 'auto', wrapperAttr: { class: 'week-options-dropdown' } }}
                        />
                    </Item>
                    <Item location="after" locateInMenu="never">
                        <div className="grid-toolbar-sep" />
                    </Item>
                    <Item location="after" locateInMenu="never">
                        <GridLayoutManager gridKey={detailGridKey} dataGrid={detailGrid} setCurrentLayout={setDetailLayout} />
                    </Item>
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>
                </div>
                </div>
              </SplitterItem>
              <SplitterItem resizable={true} collapsible={true} minSize="100px">
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Second Grid: Week Settings Dates */}
            <h6 style={{ margin: '10px 0 5px 0', fontWeight: 'bold' }}>Date / Time Entries</h6>
                <div style={{ flex: '1 1 auto', minHeight: 0 }}>
            <DataGrid
                dataSource={dateDataSource}
                ref={dateGrid}
                selectedRowKeys={selectedDateKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={onDateSelectionChanged}
                onInitNewRow={onDateInitNewRow}
                onRowInserting={onWeekSettingsDateRowInserting}
                onRowUpdating={onDateRowUpdating}
                onRowValidating={validateStartBeforeEnd}
                onEditingStart={onDateEditingStart}
                onContentReady={onDateContentReady}
                height="100%"
                noDataText={selectedDetailRowId ? "No date/time entries found" : "Select a day setting row above"}
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadStateDate}
                    storageKey={dateGridKey}
                    customSave={saveStateDate}
                />
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={{ placeholder: 'Search column' }} />
                </ColumnChooser>
                <Selection mode="multiple" showCheckBoxesMode="always" allowSelectAll={false} />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowAdding={!!selectedDetailRowId}
                    allowUpdating={!!selectedDetailRowId}
                    allowDeleting={false}
                    newRowPosition="last"
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="RowId" caption="Row ID" visible={false} showInColumnChooser={false} />
                <Column dataField="RWeeksettingsdetail" caption="Day ID" visible={false} showInColumnChooser={false} />
                <Column
                    dataField="RecordNumber"
                    caption="Record #"
                    dataType="string"
                    allowEditing={false}
                    allowSorting={false}
                />
                <Column
                    dataField="StartTime"
                    caption="Start Time"
                    dataType="datetime"
                    serializationFormat="yyyy-MM-ddTHH:mm:ss"
                    format="shortTime"
                    editorOptions={timeEditorOptions}
                    allowSorting={false}
                >
                    <RequiredRule message="Start Time value must be specified." />
                </Column>
                <Column
                    dataField="EndTime"
                    caption="End Time"
                    dataType="datetime"
                    serializationFormat="yyyy-MM-ddTHH:mm:ss"
                    format="shortTime"
                    editorOptions={timeEditorOptions}
                    allowSorting={false}
                >
                    <RequiredRule message="End Time value must be specified." />
                </Column>
                <Column
                    dataField="Description"
                    caption="Description"
                    dataType="string"
                    allowSorting={false}
                />
                <Column
                    dataField="Comment"
                    caption="Comment"
                    dataType="string"
                    allowSorting={false}
                />
                <Toolbar>
                    <Item name="addRowButton" location="after" />
                    <Item location="after" locateInMenu="never">
                        <Button
                            icon="trash"
                            hint="Delete selected"
                            stylingMode="text"
                            disabled={selectedDateKeys.length === 0}
                            onClick={onDeleteSelectedDates}
                        />
                    </Item>
                    <Item location="after" locateInMenu="never">
                        <div className="grid-toolbar-sep" />
                    </Item>
                    <Item location="after" locateInMenu="never">
                        <GridLayoutManager gridKey={dateGridKey} dataGrid={dateGrid} setCurrentLayout={setDateLayout} />
                    </Item>
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>
                </div>
                </div>
              </SplitterItem>
            </Splitter>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(WeekDaysTab);
